import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { DatePicker, FormCell, FormRow, FormSection, Select, TextInput } from '~/components';
import { ButtonFooter } from '~/components/buttons/ButtonFooter';
import { COUNTRIES, PREFIX_OPTIONS, STATES } from '../../../constants';
import { CITIZENSHIP_OPTIONS, GENDER_OPTIONS } from './constants';
import type { profileFormMachine } from './profileForm.machine';
import { PersonalFormSchema, PersonalFormValues } from './types';
import { useProfileDraftForm, type ProfileDrafts } from './useProfileDraftForm';
import { VisaSection } from './VisaSection';

const TAGS = CT.profile.personalInfo.form;

interface InfoProps {
  values: Partial<PersonalFormValues>;
  actor: ActorRefFrom<typeof profileFormMachine>;
  drafts?: ProfileDrafts;
}

export const PersonalInfoForm = ({ actor, drafts }: InfoProps) => {
  const personalInfo = useSelector(actor, (state) => state.context.personalInfo);
  // Saved values stay reactive after a save. The Profile draft wrapper also
  // restores local edits when returning to a section whose saved values are unchanged.
  const methods = useProfileDraftForm<PersonalFormValues>(
    {
      resolver: zodResolver(PersonalFormSchema),
      values: personalInfo,
    },
    drafts?.personal
  );
  const { handleSubmit, setValue, watch } = methods;

  const citizenship = watch('citizenshipStatus');
  const birthCountry = watch('birthCountryCode');
  const birthState = watch('birthState');
  const isUsBirthCountry = birthCountry?.value === 'US';

  useEffect(() => {
    if (!isUsBirthCountry && birthState?.value) {
      setValue('birthState', { label: '', value: '' }, { shouldDirty: true, shouldValidate: true });
    }
  }, [birthState?.value, isUsBirthCountry, setValue]);

  const onSubmitSuccess = async (values: PersonalFormValues) => {
    actor.send({ type: 'ON_SUBMIT_PERSONAL', values });
  };
  const onSubmitError = (err: unknown) => console.debug('PersonalInfo form validation error:', err);

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-5"
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        data-cy={TAGS.id}
        id={TAGS.id}
      >
        <FormSection componentsPerLine={1}>
          <FormCell span={2}>
            <FormRow>
              <Select
                wrapValueOnMobile
                name={'prefixName'}
                label={'Prefix'}
                cyTag={TAGS.prefixDropdown}
                options={PREFIX_OPTIONS}
              />
            </FormRow>
          </FormCell>
          <FormCell span={3}>
            <FormRow>
              <TextInput
                name={'firstName'}
                cyTag={TAGS.firstNameInput}
                label={'First name'}
                placeholder={'John'}
                required
              />
            </FormRow>
          </FormCell>
          <FormCell span={3}>
            <FormRow>
              <TextInput
                name={'lastName'}
                cyTag={TAGS.lastNameInput}
                label={'Last Name'}
                placeholder={'Doe'}
                required
              />
            </FormRow>
          </FormCell>
          <FormCell span={2}>
            <FormRow>
              <TextInput
                name={'middleName'}
                cyTag={TAGS.middleNameInput}
                label={'Middle Name'}
                placeholder={'Middle'}
              />
            </FormRow>
          </FormCell>
          <FormCell span={2}>
            <FormRow className="text-black">
              <TextInput
                name={'suffixName'}
                cyTag={TAGS.suffixDropdown}
                label={'Suffix'}
                placeholder={'PhD'}
              />
            </FormRow>
          </FormCell>
        </FormSection>
        <FormSection componentsPerLine={2}>
          <FormRow>
            <DatePicker
              label="Date of Birth"
              cyTag={TAGS.dateOfBirth}
              name={'dateOfBirth'}
              required
            />
          </FormRow>
          <FormRow>
            <Select
              wrapValueOnMobile
              name={'gender'}
              label={'Gender'}
              cyTag={TAGS.gender}
              options={GENDER_OPTIONS}
              required
            />
          </FormRow>
        </FormSection>
        <FormSection componentsPerLine={3}>
          <FormRow>
            <Select
              wrapValueOnMobile
              name={'birthCountryCode'}
              label={'Birth Country'}
              cyTag={TAGS.birthCountry}
              options={COUNTRIES}
              required
            />
          </FormRow>
          <FormRow>
            <Select
              wrapValueOnMobile
              name={'birthState'}
              cyTag={TAGS.birthState}
              label={'Birth State'}
              options={STATES}
              disabled={!isUsBirthCountry}
              required={isUsBirthCountry}
            />
          </FormRow>
          <FormRow>
            <TextInput
              name={'birthCity'}
              cyTag={TAGS.birthCity}
              label={'Birth City'}
              placeholder={'City'}
              required
            />
          </FormRow>
        </FormSection>
        <FormSection componentsPerLine={2}>
          <FormRow>
            <Select
              wrapValueOnMobile
              name={'citizenshipStatus'}
              label={'Citizenship'}
              cyTag={TAGS.citizenshipDropdown}
              options={CITIZENSHIP_OPTIONS}
              required
            />
          </FormRow>
        </FormSection>
        {citizenship?.value != 'United States' && <VisaSection />}
        <ButtonFooter primaryButton={{ label: 'Save', cyTag: TAGS.submitBtn, isSubmit: true }} />
      </form>
    </FormProvider>
  );
};
